export const SITE = {
  name: 'HealthShopLongevity',
  legalName: 'Balıkesir Güven Özel Sağlık Laboratuvar Hizmetleri Ltd. Şti.',
  titleSuffix: 'HealthShopLongevity',
  description:
    'tDCS, CES, hidrojenli su ve LED ışık terapisi cihazları. Health Shop Longevity ile sağlıklı yaşam ve longevity ürünlerini güvenle satın alın.',
  url: import.meta.env.VITE_SITE_URL?.replace(/\/$/, '') || 'https://healthshoplongevity.com',
  locale: 'tr_TR',
  language: 'tr',
  email: 'info@irgilakademi.com',
  phone: '+905535382462',
  address: {
    street: 'Bahçelievler Mah. Atatürk Cad. No: 10B',
    city: 'Balıkesir',
    region: 'Altıeylül',
    country: 'TR',
  },
  defaultImage: 'https://healthshoplongevity.com/img/tdcs1.png',
  twitterHandle: '@healthshoplongevity',
} as const;

export type SeoConfig = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'product' | 'article';
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export const DEFAULT_SEO: Required<Pick<SeoConfig, 'title' | 'description' | 'type'>> & SeoConfig = {
  title: 'Sağlık & Longevity Cihazları',
  description: SITE.description,
  type: 'website',
};

const NOINDEX_PATHS = [
  '/cart',
  '/checkout',
  '/login',
  '/register',
  '/orders',
  '/addresses',
  '/profile',
  '/admin',
  '/auth',
];

export function buildCanonical(path = '/'): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE.url}${normalized === '/' ? '' : normalized}`;
}

export function buildTitle(pageTitle?: string): string {
  if (!pageTitle) return `${DEFAULT_SEO.title} | ${SITE.titleSuffix}`;
  return `${pageTitle} | ${SITE.titleSuffix}`;
}

export function getRouteSeo(pathname: string): SeoConfig {
  if (NOINDEX_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
    return { noindex: true, title: 'Hesap', description: SITE.description };
  }

  if (pathname.startsWith('/product/')) {
    return {
      title: 'Ürün Detayı',
      description: SITE.description,
      path: pathname,
      type: 'product',
    };
  }

  if (pathname.startsWith('/yasal/')) {
    const slug = pathname.replace('/yasal/', '');
    const legal = LEGAL_SEO[slug];
    return legal ?? { title: 'Yasal Metin', description: SITE.description, path: pathname };
  }

  return ROUTE_SEO[pathname] ?? DEFAULT_SEO;
}

const ROUTE_SEO: Record<string, SeoConfig> = {
  '/': {
    title: 'Sağlıklı Yaşam, Uzun Ömür',
    description:
      'tDCS, CES, hidrojenli su ve ışık terapisi cihazları. Online teknik destek ile güvenle alışveriş yapın.',
    path: '/',
  },
  '/shop': {
    title: 'Ürünler',
    description:
      'tDCS, CES, hidrojenli su ve LED ışık terapisi cihazları. Premium sağlık ürünlerini keşfedin.',
    path: '/shop',
  },
  '/cart': {
    title: 'Sepet',
    noindex: true,
  },
  '/checkout': {
    title: 'Ödeme',
    noindex: true,
  },
  '/login': {
    title: 'Giriş Yap',
    noindex: true,
  },
  '/register': {
    title: 'Kayıt Ol',
    noindex: true,
  },
  '/auth/verify-email': {
    title: 'E-posta Doğrulama',
    noindex: true,
  },
  '/auth/verify-email-pending': {
    title: 'E-posta Doğrulama',
    noindex: true,
  },
  '/auth/forgot-password': {
    title: 'Şifremi Unuttum',
    noindex: true,
  },
  '/auth/reset-password': {
    title: 'Şifre Sıfırlama',
    noindex: true,
  },
  '/orders': {
    title: 'Siparişlerim',
    noindex: true,
  },
  '/addresses': {
    title: 'Adreslerim',
    noindex: true,
  },
  '/profile': {
    title: 'Profilim',
    noindex: true,
  },
  '/cerez-politikasi': {
    title: 'Çerez Politikası',
    description: 'Health Shop Longevity çerez politikası ve çerez tercihleri hakkında bilgilendirme.',
    path: '/cerez-politikasi',
  },
};

const LEGAL_SEO: Record<string, SeoConfig> = {
  'kvkk-aydinlatma-metni': {
    title: 'KVKK Aydınlatma Metni',
    description: 'Kişisel verilerin korunmasına ilişkin aydınlatma metni.',
    path: '/yasal/kvkk-aydinlatma-metni',
  },
  'kullanim-sozlesmesi': {
    title: 'Web Sitesi Kullanım Sözleşmesi',
    description: 'Health Shop Longevity web sitesi kullanım koşulları.',
    path: '/yasal/kullanim-sozlesmesi',
  },
  'mesafeli-satis-sozlesmesi': {
    title: 'Mesafeli Satış Sözleşmesi',
    description: '6502 sayılı kanun kapsamında mesafeli satış sözleşmesi.',
    path: '/yasal/mesafeli-satis-sozlesmesi',
  },
  'iptal-iade-kosullari': {
    title: 'Cayma – İptal ve İade Koşulları',
    description: 'Sipariş iptali, cayma hakkı ve iade koşulları.',
    path: '/yasal/iptal-iade-kosullari',
  },
  'cayma-formu': {
    title: 'Cayma Formu',
    description: 'Mesafeli satış sözleşmesinden cayma hakkı formu.',
    path: '/yasal/cayma-formu',
  },
};

export const SITEMAP_PATHS = [
  '/',
  '/shop',
  '/product/tdcs',
  '/product/ces',
  '/product/nord',
  '/product/light',
  '/cerez-politikasi',
  '/yasal/kvkk-aydinlatma-metni',
  '/yasal/kullanim-sozlesmesi',
  '/yasal/mesafeli-satis-sozlesmesi',
  '/yasal/iptal-iade-kosullari',
  '/yasal/cayma-formu',
] as const;
